import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  registerOverlay,
  unregisterOverlay,
  closeTopOverlay,
  hasOverlay,
  goPageBack,
} from '../utils/backNavigation';

/**
 * Register an overlay (modal / drawer / panel / FAB menu) with the global
 * step-by-step back manager while `enabled` is true.
 *
 * - Opening the first overlay pushes ONE shared history guard, so a
 *   hardware / browser / swipe back press closes the top overlay instead
 *   of leaving the page.
 * - Stacked overlays close one-per-press (top first).
 * - Manual closes (X, backdrop, Escape) unwind the guard automatically.
 *
 * @param {boolean} enabled - overlay is open
 * @param {Function} onClose - idempotent close callback
 * @param {string} label - debug label
 */
export function useOverlayBack(enabled, onClose, label = 'overlay') {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!enabled) return undefined;
    const stableClose = () => {
      try {
        closeRef.current?.();
      } catch {
        /* close must never break back navigation */
      }
    };
    const id = registerOverlay(stableClose, label);
    return () => unregisterOverlay(id);
  }, [enabled, label]);
}

/**
 * In-app back button handler with overlay-first UX:
 *  1st press closes the topmost modal/drawer/panel (if any),
 *  otherwise goes one page back, with a safe fallback for direct launches.
 *
 * @param {string} fallback - route when history is empty (default /dashboard)
 * @returns {{ goBack: Function, hasOverlay: Function }}
 */
export function useSmartBack(fallback = '/dashboard') {
  const navigate = useNavigate();
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;

  const goBack = useCallback(() => {
    if (closeTopOverlay()) return 'overlay';
    goPageBack(navigate, fallbackRef.current);
    return 'page';
  }, [navigate]);

  return { goBack, hasOverlay };
}

export default useSmartBack;
